import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import ZoomableSVGContainer from './ZoomableSVGContainer';
import { Link2Off, Hammer, Shield, Coins, TrendingUp, Unlock, Crown } from 'lucide-react';

/**
 * ProsperMind — Ladder of Wealth (Exact Diagram, Responsive)
 * - 7 steps: Dependence, Productivity, Independence, Stability, Security, Freedom, Abundance
 * - Exact visual language matching the provided diagram
 * - Responsive via SVG viewBox
 * - Highlights the user's current stage
 * - Now supports internationalization
 * - Stage-specific icons added
 */

// Stage Icons mapping
const STAGE_ICONS = {
  1: Link2Off,      // Broken chain for Dependence
  2: Hammer,        // Hammer for Productivity
  3: Shield,        // Shield for Stability (Independence)
  4: Coins,         // Coins for Stability (renamed from Independence)
  5: TrendingUp,    // Trending chart for Security (Growth)
  6: Unlock,        // Open lock for Freedom
  7: Crown,         // Crown for Abundance/Legacy
};

const COLORS = {
  navy: "#1E3A8A",
  emerald: "#10B981",
  coral: "#EF4444",
  neutral: "#000000", // Changed to black background
  gold: "#F59E0B",
  purple: "#8B5CF6",
  blue: "#3B82F6",
  yellow: "#EAB308",
  red: "#DC2626",
  darkRed: "#8B0000", // Dark red from bankruptcy box
  darkBlue: "#1E40AF",
  darkerBlue: "#1E3A8A", // Even darker blue for decisiveness and blue arrow
  royalBlue: "#4169E1", // Royal blue for Freedom stage
  orange: "#EA580C",
  gray: "#6B7280",
  gray300: "#D1D5DB",
  ink: "#111827",
  green: "#059669",
};

const STAGES = [
  { 
    key: 1, 
    label: "Dependence", 
    color: "#DC2626", // Vibrant red
    description: "No income, you depend on others to cover expenses. Limited ability to make decisions"
  },
  { 
    key: 2, 
    label: "Productivity", 
    color: "#EA580C", // Vibrant orange
    description: "By exploiting your skills, you have sources of income, but don't cover your expenses"
  },
  { 
    key: 3, 
    label: "Independence", 
    color: "#F59E0B", // Vibrant yellow/amber
    description: "Your income covers your expenses, sometimes you can save money"
  },
  { 
    key: 4, 
    label: "Stability", 
    color: "#84CC16", // Vibrant lime green
    description: "Savings are now generated in a stable way, and you also generate an extra income from savings"
  },
  { 
    key: 5, 
    label: "Security", 
    color: "#059669", // Same green as money cycle boxes
    description: "Income from investments (rents or interests) cover your basic expenses. You don't have expensive debt, and can diversify investments."
  },
  { 
    key: 6, 
    label: "Freedom", 
    color: "#1E3A8A", // Same blue as decisiveness box
    description: "Income from your investments cover all of your expenses with your lifestyle and a bit more. You don't have to work for money anymore"
  },
  { 
    key: 7, 
    label: "Abundance", 
    color: "#8B5CF6", // Vibrant purple
    description: "Income from your investment and business cover far beyond your lifestyle. Money is not a problem anymore, you start thinking about legacy."
  },
];

const TopBanner = ({ x, y, text, hoveredBanner, setHoveredBanner, t }) => {
  // Split subtitle into two lines for better readability
  const subtitleText = text.split(',').slice(1).join(', ');
  const words = subtitleText.split(' ');
  const midpoint = Math.ceil(words.length / 2);
  const firstLine = words.slice(0, midpoint).join(' ');
  const secondLine = words.slice(midpoint).join(' ');

  return (
    <g
      style={{
        transformOrigin: `${x + 450}px ${y + 55}px`,
        transform: hoveredBanner === 'top' ? 'scale(1.05)' : 'scale(1)',
        transition: 'transform 0.3s ease',
        cursor: 'pointer'
      }}
      onMouseEnter={() => setHoveredBanner('top')}
      onMouseLeave={() => setHoveredBanner(null)}
    >
      <rect 
        x={x} 
        y={y} 
        rx={8} 
        ry={8} 
        width={900} 
        height={110} 
        fill={COLORS.darkerBlue}
        style={{
          filter: hoveredBanner === 'top' ? 
            'drop-shadow(0 8px 24px rgba(30, 58, 138, 0.7))' : 
            'drop-shadow(0 2px 6px rgba(30, 58, 138, 0.3))'
        }}
      />
      <text x={x + 450} y={y + 35} fontSize={32} fontWeight="700" fill="white" textAnchor="middle">
        {t('charts.ladderOfWealth.bannerTopTitle')}
      </text>
      <text x={x + 450} y={y + 65} fontSize={24} fontWeight="600" fill="white" textAnchor="middle">
        {firstLine}
      </text>
      <text x={x + 450} y={y + 90} fontSize={24} fontWeight="600" fill="white" textAnchor="middle">
        {secondLine}
      </text>
    </g>
  );
};

const BottomBanner = ({ x, y, text, hoveredBanner, setHoveredBanner, t }) => {
  // Get the subtitle text (everything after the first comma)
  const subtitleText = text.split(',').slice(1).join(',').trim();

  return (
    <g
      style={{
        transformOrigin: `${x + 450}px ${y + 55}px`,
        transform: hoveredBanner === 'bottom' ? 'scale(1.05)' : 'scale(1)',
        transition: 'transform 0.3s ease',
        cursor: 'pointer'
      }}
      onMouseEnter={() => setHoveredBanner('bottom')}
      onMouseLeave={() => setHoveredBanner(null)}
    >
      <rect 
        x={x} 
        y={y} 
        rx={8} 
        ry={8} 
        width={900} 
        height={110} 
        fill={COLORS.darkRed}
        style={{
          filter: hoveredBanner === 'bottom' ? 
            'drop-shadow(0 8px 24px rgba(139, 0, 0, 0.7))' : 
            'drop-shadow(0 2px 6px rgba(139, 0, 0, 0.3))'
        }}
      />
      <text x={x + 450} y={y + 32} fontSize={32} fontWeight="700" fill="white" textAnchor="middle">
        {t('charts.ladderOfWealth.bannerBottomTitle')}
      </text>
      <text x={x + 450} y={y + 68} fontSize={20} fontWeight="500" fill="white" textAnchor="middle">
        {subtitleText}
      </text>
    </g>
  );
};

export const LadderOfWealthChart = ({ stage, hasData = true, diagnosisCompleted = false, userStage = null, quizStageLevel = null, quizDirection = null, onRetakeQuiz = null, isPremium = true, hideTitle = false, compact = false, focusStage = null, hideInfoBox = false }) => {
  const { t } = useTranslation();
  const [hoveredStage, setHoveredStage] = useState(null);
  const [hoveredBanner, setHoveredBanner] = useState(null);
  const [isLandscape, setIsLandscape] = useState(false);
  
  // Detect orientation changes for mobile
  useEffect(() => {
    const checkOrientation = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };
    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);
    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  // Use quiz stage level if available, otherwise fall back to diagnosis stage
  const activeStageLevel = quizStageLevel || stage;

  // Create stages array with translated content and quiz level names
  const STAGES = [
    { 
      key: 1, 
      name: "dependence",
      quizLevel: t('charts.ladderOfWealth.quizLevels.supported', 'Supported'),
      label: t('charts.ladderOfWealth.stages.dependence.label'),
      color: "#8B0000", // Dark red to match bankruptcy box
      description: t('charts.ladderOfWealth.stages.dependence.description')
    },
    { 
      key: 2, 
      name: "productivity",
      quizLevel: t('charts.ladderOfWealth.quizLevels.builder', 'Builder'),
      label: t('charts.ladderOfWealth.stages.productivity.label'),
      color: "#EA580C", // Vibrant orange
      description: t('charts.ladderOfWealth.stages.productivity.description')
    },
    { 
      key: 3, 
      name: "independence",
      quizLevel: t('charts.ladderOfWealth.quizLevels.stabilizer', 'Stabilizer'),
      label: "Stability", // Changed from Independence
      color: "#F59E0B", // Vibrant yellow/amber
      description: t('charts.ladderOfWealth.stages.independence.description')
    },
    { 
      key: 4, 
      name: "stability",
      quizLevel: t('charts.ladderOfWealth.quizLevels.accumulator', 'Accumulator'),
      label: "Independence", // Changed from Stability
      color: "#84CC16", // Vibrant lime green
      description: t('charts.ladderOfWealth.stages.stability.description')
    },
    { 
      key: 5, 
      name: "security",
      quizLevel: t('charts.ladderOfWealth.quizLevels.investor', 'Investor'),
      label: "Growth", // Changed from Security
      color: "#059669", // Same green as money cycle boxes
      description: t('charts.ladderOfWealth.stages.security.description')
    },
    { 
      key: 6, 
      name: "freedom",
      quizLevel: t('charts.ladderOfWealth.quizLevels.lifeOwner', 'Life Owner'),
      label: t('charts.ladderOfWealth.stages.freedom.label'),
      color: "#4169E1", // Royal blue for better readability
      description: t('charts.ladderOfWealth.stages.freedom.description')
    },
    { 
      key: 7, 
      name: "abundance",
      quizLevel: t('charts.ladderOfWealth.quizLevels.legacyDesigner', 'Legacy Designer'),
      label: t('charts.ladderOfWealth.stages.abundance.label'),
      color: "#6B46C1", // Darker purple
      description: t('charts.ladderOfWealth.stages.abundance.description')
    },
  ];

  // Find user's diagnosed stage for golden arrow positioning
  const getUserStageIndex = () => {
    if (!diagnosisCompleted || !userStage) return -1;
    return STAGES.findIndex(s => s.name === userStage.toLowerCase());
  };
  
  const userStageIndex = getUserStageIndex();

  return (
    <div style={{ 
      background: COLORS.neutral, 
      borderRadius: 12, 
      padding: window.innerWidth < 768 ? '4px 4px 12px 4px' : 16 
    }}>
      {/* Calculate viewBox based on mode and orientation */}
      {(() => {
        // For portrait mode with focus stage, calculate viewBox to center on that stage
        let viewBox = hideTitle ? "0 200 1600 1000" : "0 0 1600 1200";
        let containerHeight = window.innerWidth >= 1024 ? "min(70vh, 650px)" : "min(60vh, 500px)";
        let mobileZoom = 1.8;
        
        if (compact) {
          if (isLandscape) {
            // Landscape: show full ladder
            viewBox = hideTitle ? "0 380 1600 800" : "0 0 1600 1200";
            containerHeight = "min(55vh, 450px)";
            mobileZoom = 1.0;
          } else if (focusStage) {
            // Portrait with focus: show full ladder with arrow above Accumulator
            // Direction arrow is always above Accumulator (stage 5, index 4)
            // Arrow position: x ~930 (center of Accumulator), y = 330
            // Show full ladder from arrow to bottom stages
            viewBox = "0 200 1600 900";
            containerHeight = "min(60vh, 500px)";
            mobileZoom = 1.0;
          } else {
            // Portrait without focus: show full ladder
            // Adjust viewBox when title is hidden to reduce top spacing significantly
            viewBox = hideTitle ? "0 380 1600 800" : "0 0 1600 1200";
            containerHeight = "min(50vh, 400px)";
            mobileZoom = 1.3;
          }
        }
        
        return (
          <div style={{ width: "100%", height: containerHeight }}>
            <ZoomableSVGContainer initialMobileZoom={mobileZoom}>
              <svg viewBox={viewBox} width="100%" height="100%" role="img"
                 aria-label="ProsperMind Ladder of Wealth — seven steps from Dependence to Abundance with current stage highlighted."
                 style={{ overflow: 'visible' }}>

              {/* Title - with smaller font for longer translations */}
              {!hideTitle && (
                <text x="800" y="80" fontSize="84" fontWeight="bold" fill="white" textAnchor="middle">
                  {t('charts.ladderOfWealth.title')}
                </text>
              )}

          {/* Gradient definitions for direction arrows */}
          <defs>
            {/* Blur filter for non-premium users */}
            <filter id="blurFilter" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="8" />
            </filter>
            <linearGradient id="blueArrowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#1E40AF" />
              <stop offset="50%" stopColor="#3B82F6" />
              <stop offset="100%" stopColor="#1E40AF" />
            </linearGradient>
            <filter id="blueArrowShadow">
              <feDropShadow dx="2" dy="4" stdDeviation="4" floodColor="rgba(30, 64, 175, 0.5)" />
            </filter>
            <linearGradient id="redArrowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#991B1B" />
              <stop offset="50%" stopColor="#DC2626" />
              <stop offset="100%" stopColor="#991B1B" />
            </linearGradient>
            <filter id="redArrowShadow">
              <feDropShadow dx="2" dy="4" stdDeviation="4" floodColor="rgba(153, 27, 27, 0.5)" />
            </filter>
            <linearGradient id="goldArrowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#B45309" />
              <stop offset="50%" stopColor="#F59E0B" />
              <stop offset="100%" stopColor="#B45309" />
            </linearGradient>
            <filter id="goldArrowShadow">
              <feDropShadow dx="2" dy="4" stdDeviation="4" floodColor="rgba(180, 83, 9, 0.5)" />
            </filter>
            <linearGradient id="grayGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#4B5563" />
              <stop offset="50%" stopColor="#9CA3AF" />
              <stop offset="100%" stopColor="#4B5563" />
            </linearGradient>
            <filter id="grayShadow">
              <feDropShadow dx="2" dy="4" stdDeviation="4" floodColor="rgba(107, 114, 128, 0.5)" />
            </filter>
          </defs>

          {/* Direction Arrow - Always positioned above Accumulator stage (stage 4, key 4) in the middle */}
          {quizDirection && quizStageLevel && (() => {
            // Always position above Accumulator (key 4, index 3 in STAGES array)
            // Stage positions by quizLevel: Supported(0), Builder(1), Stabilizer(2), Accumulator(3), Investor(4), LifeOwner(5), LegacyDesigner(6)
            const accumulatorIndex = 3; // Accumulator is at index 3 (key 4)
            const stepW = 220;
            // Calculate x position for Accumulator stage: 50 + index * 220
            const stageX = 50 + (accumulatorIndex * stepW) + (stepW / 2); // Center of the Accumulator stage box
            const stageY = 850 - accumulatorIndex * 80; // Y position of Accumulator stage
            // Ladder angle: atan(80/220) ≈ 20 degrees
            const ladderAngle = -20;
            
            // Arrow dimensions for proper centering
            const advancingWidth = 320; // rect(280) + arrowhead(40)
            const contractingWidth = 320; // arrowhead(40) + rect(280)
            const steadyWidth = 250; // pill only, no arrowhead
            
            // Position above the Accumulator stage
            const directionY = stageY - 200;
            
            return (
              <g>
                {quizDirection === 'advancing' && (
                  <g transform={`translate(${stageX - advancingWidth/2}, ${directionY}) rotate(${ladderAngle}, ${advancingWidth/2}, 40)`}>
                    {/* Blue "Going Up" arrow - arrowhead on RIGHT */}
                    <rect x="0" y="15" width="280" height="50" rx="25" fill="url(#blueArrowGradient)" filter="url(#blueArrowShadow)" />
                    <polygon points="260,0 320,40 260,80" fill="url(#blueArrowGradient)" filter="url(#blueArrowShadow)" />
                    <text x="140" y="50" fontSize="22" fontWeight="700" fill="white" textAnchor="middle">GOING UP</text>
                  </g>
                )}
                {quizDirection === 'contracting' && (
                  <g transform={`translate(${stageX - contractingWidth/2 + 20}, ${directionY}) rotate(${ladderAngle}, ${contractingWidth/2 - 20}, 40)`}>
                    {/* Red "Going Down" arrow - arrowhead on LEFT (flipped) */}
                    <polygon points="20,0 -40,40 20,80" fill="url(#redArrowGradient)" filter="url(#redArrowShadow)" />
                    <rect x="0" y="15" width="280" height="50" rx="25" fill="url(#redArrowGradient)" filter="url(#redArrowShadow)" />
                    <text x="140" y="50" fontSize="20" fontWeight="700" fill="white" textAnchor="middle">GOING DOWN</text>
                  </g>
                )}
                {quizDirection === 'steady' && (
                  <g transform={`translate(${stageX - steadyWidth/2}, ${directionY})`}>
                    {/* Gray/Silver pill without arrowhead - for steady/stagnant */}
                    <rect x="0" y="15" width="250" height="50" rx="25" fill="url(#grayGradient)" filter="url(#grayShadow)" />
                    {/* Use "STAGNANT" for first 3 stages, "STEADY" for stages 4+ */}
                    <text x="125" y="50" fontSize="22" fontWeight="700" fill="white" textAnchor="middle">
                      {quizStageLevel < 4 ? 'STAGNANT' : 'STEADY'}
                    </text>
                  </g>
                )}
              </g>
            );
          })()}

          {/* Steps (left→right, each higher than the previous, corner-to-corner) */}
          {/* Sort stages so active one renders last (on top) */}
          {[...STAGES].sort((a, b) => {
            const aActive = quizStageLevel && a.key === quizStageLevel;
            const bActive = quizStageLevel && b.key === quizStageLevel;
            if (aActive) return 1;  // Active stage goes last
            if (bActive) return -1;
            return STAGES.indexOf(a) - STAGES.indexOf(b); // Keep original order otherwise
          }).map((s) => {
            const i = STAGES.indexOf(s); // Get original index for positioning
            
            // Original diagonal staircase layout
            const stepW = 220;
            const stepH = 280;
            
            // Original diagonal layout - consistent for all modes
            let x = 50;
            for (let j = 0; j < i; j++) {
              x += 220;
            }
            const y = 850 - i * 80;
            
            // Active if quiz stage level matches this step's key
            const active = quizStageLevel && s.key === quizStageLevel;
            
            // Determine if this stage should be blurred (non-premium and not user's stage)
            const shouldBlur = !isPremium && !active;
            
            // Solid colors for all stages
            const fillColor = s.color; // Always use full solid color
            const strokeColor = s.color;
            // White text for ALL steps
            const textColor = "white";
            
            const isHovered = hoveredStage === s.key;
            
            return (
              <g key={s.key}
                 style={{
                   transformOrigin: `${x + stepW/2}px ${y + stepH/2}px`,
                   transform: isHovered && !shouldBlur ? 'scale(1.2)' : 'scale(1)',
                   transition: 'transform 0.3s ease',
                   filter: shouldBlur ? 'url(#blurFilter)' : 'none',
                   opacity: shouldBlur ? 0.7 : 1
                 }}
                 onMouseEnter={() => !shouldBlur && setHoveredStage(s.key)}
                 onMouseLeave={() => setHoveredStage(null)}
              >
                {/* Quiz Level Name - displayed above the step */}
                <text
                  x={x + stepW/2}
                  y={y - 12}
                  fontSize={22}
                  fontWeight="700"
                  fill={s.color}
                  textAnchor="middle"
                  style={{
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    pointerEvents: 'none'
                  }}
                >
                  {s.quizLevel}
                </text>

                {/* User position arrow - shown for active stage from quiz */}
                {active && quizStageLevel && (
                  <g>
                    {/* Gradient definition for fading effect */}
                    <defs>
                      <linearGradient id={`fadeGradient-${s.key}`} x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor={s.color} stopOpacity="0" />
                        <stop offset="30%" stopColor={s.color} stopOpacity="1" />
                        <stop offset="70%" stopColor={s.color} stopOpacity="1" />
                        <stop offset="100%" stopColor={s.color} stopOpacity="0" />
                      </linearGradient>
                      <linearGradient id={`labelGradient-${s.key}`} x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor={s.color} stopOpacity="0.3" />
                        <stop offset="20%" stopColor={s.color} stopOpacity="1" />
                        <stop offset="80%" stopColor={s.color} stopOpacity="1" />
                        <stop offset="100%" stopColor={s.color} stopOpacity="0.3" />
                      </linearGradient>
                    </defs>
                    {/* Arrow pointing to current stage - same color as stage */}
                    <path
                      d={`M ${x + stepW/2 - 20} ${y - 85} L ${x + stepW/2} ${y - 60} L ${x + stepW/2 + 20} ${y - 85} Z`}
                      fill={s.color}
                      stroke="white"
                      strokeWidth={2}
                      style={{
                        filter: `drop-shadow(0 4px 12px ${s.color}90)`,
                        animation: 'bounce 1s ease-in-out infinite'
                      }}
                    />
                    {/* "YOU ARE HERE" label with fading sides */}
                    <rect
                      x={x + stepW/2 - 70}
                      y={y - 125}
                      width={140}
                      height={30}
                      rx={15}
                      fill={`url(#labelGradient-${s.key})`}
                      stroke="white"
                      strokeWidth={1.5}
                      strokeOpacity={0.8}
                      style={{
                        filter: `drop-shadow(0 4px 12px ${s.color}70)`
                      }}
                    />
                    <text
                      x={x + stepW/2}
                      y={y - 105}
                      fontSize={14}
                      fontWeight="700"
                      fill="white"
                      textAnchor="middle"
                      style={{
                        textShadow: `0 1px 2px rgba(0,0,0,0.5)`
                      }}
                    >
                      YOU ARE HERE
                    </text>
                  </g>
                )}

                {/* Step rectangle with solid coloring and hover effect */}
                <rect
                  x={x}
                  y={y}
                  width={stepW}
                  height={stepH}
                  rx={8}
                  ry={8}
                  fill={fillColor}
                  stroke={active ? "white" : strokeColor}
                  strokeWidth={active ? 4 : (isHovered ? 3 : 2)}
                  style={{ 
                    filter: active ? `drop-shadow(0 0 20px ${s.color}) drop-shadow(0 6px 12px ${s.color}50)` : 
                           isHovered ? `drop-shadow(0 8px 24px ${s.color}70)` :
                           `drop-shadow(0 2px 6px ${s.color}30)`,
                    cursor: 'pointer'
                  }}
                />
                
                {/* Step label - scales with parent group */}
                <text
                  x={x + stepW/2}
                  y={y + 38}
                  fontSize={24}
                  fontWeight="700"
                  fill={textColor}
                  textAnchor="middle"
                  style={{
                    cursor: 'pointer',
                    pointerEvents: 'none'
                  }}
                >
                  {s.label}
                </text>

                {/* Stage Icon - positioned to the left of the label */}
                <foreignObject 
                  x={x + 15} 
                  y={y + 15} 
                  width={36} 
                  height={36}
                  style={{ 
                    cursor: 'pointer',
                    pointerEvents: 'none'
                  }}
                >
                  {(() => {
                    const IconComponent = STAGE_ICONS[s.key];
                    return IconComponent ? (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '36px',
                        height: '36px',
                        backgroundColor: active ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)',
                        borderRadius: '8px'
                      }}>
                        <IconComponent 
                          style={{ 
                            width: '22px', 
                            height: '22px', 
                            color: textColor,
                            opacity: active ? 1 : 0.8
                          }} 
                        />
                      </div>
                    ) : null;
                  })()}
                </foreignObject>

                {/* Description text inside the box - scales with parent group */}
                <foreignObject 
                  x={x + 10} 
                  y={y + 48} 
                  width={stepW - 20} 
                  height={stepH - 58}
                  style={{ 
                    cursor: 'pointer',
                    pointerEvents: 'none'
                  }}
                >
                  <div style={{
                    fontSize: '19px',
                    color: textColor,
                    textAlign: 'center',
                    lineHeight: '1.35',
                    padding: '6px',
                    fontWeight: '400',
                    overflow: 'hidden'
                  }}>
                    {s.description}
                  </div>
                </foreignObject>
                
                {/* Connection lines removed per user request */}
              </g>
            );
          })}

        </svg>
        </ZoomableSVGContainer>
      </div>
        );
      })()}

      {/* "To determine your stage" message - only shown when diagnosis is NOT completed and hideInfoBox is false */}
      {!diagnosisCompleted && !hideInfoBox && (
        <div style={{ 
          textAlign: 'center', 
          padding: '12px', 
          backgroundColor: '#1F2937', 
          borderRadius: '6px', 
          marginTop: '15px',
          border: '1px solid #F59E0B',
          maxWidth: '400px',
          margin: '15px auto 0 auto'
        }}>
          <p style={{ color: '#FCD34D', margin: 0, fontSize: '12px' }}>
            {t('charts.ladderOfWealth.noDataMessage')}
          </p>
        </div>
      )}

      {/* Retake Quiz button - shown when quiz has been completed */}
      {quizStageLevel && onRetakeQuiz && (
        <div style={{ 
          display: 'flex',
          justifyContent: 'center',
          marginTop: '20px'
        }}>
          <button
            onClick={onRetakeQuiz}
            data-testid="ladder-retake-quiz-btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              backgroundColor: '#1F2937',
              color: 'white',
              border: 'none',
              borderRadius: '9999px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#374151';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#1F2937';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
              <path d="M3 3v5h5"/>
            </svg>
            Retake Quiz
          </button>
        </div>
      )}
    </div>
  );
};

export default LadderOfWealthChart;